import { useMemo, useState } from "react";
import { ProgressDots } from "./QuizDuell";

/* Lückentext — ARENA edition. The sentence is the hero (huge type), the blank
   glows in the accent colour and POPS when filled. Calls onFinish({correct,total}). */
export default function Lueckentext({ module, accent = "#22c55e", onFinish }) {
  const items = useMemo(() => (module.items || []).map((it) => ({
    ...it, _opts: [...it.options].sort(() => Math.random() - 0.5),
  })), [module.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);

  const it = items[idx];
  const locked = picked !== null;
  const correct = locked && picked === it.answer;
  const [before, after] = (it.text || "").split("⬚");

  const choose = (opt) => { if (locked) return; setPicked(opt); if (opt === it.answer) setScore((s) => s + 1); };
  const next = () => {
    if (idx + 1 < items.length) { setIdx((i) => i + 1); setPicked(null); }
    else onFinish({ correct: score, total: items.length });
  };

  return (
    <div className="mx-auto w-full max-w-3xl">
      <ProgressDots total={items.length} idx={idx} accent={accent} />

      <div key={idx} className="mt-5 rounded-[2rem] border p-6 backdrop-blur-xl sm:p-9"
        style={{
          animation: "arena-in 0.35s ease both",
          borderColor: locked ? (correct ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)") : `${accent}33`,
          background: `linear-gradient(165deg, ${accent}0e 0%, rgba(8,6,26,0.85) 55%)`,
          boxShadow: `0 0 44px ${accent}1c, 0 20px 50px rgba(0,0,0,0.45)`,
        }}>
        <span className="rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest" style={{ background: `${accent}1c`, color: accent, border: `1px solid ${accent}40` }}>
          Lücke {idx + 1} / {items.length}
        </span>
        {module.prompt && idx === 0 && <p className="mt-2 text-sm text-white/55">{module.prompt}</p>}

        {/* the sentence — hero typography */}
        <p className="mt-6 text-2xl font-black leading-relaxed sm:text-4xl sm:leading-relaxed">
          {before}
          <span
            className="mx-2 inline-block min-w-[90px] rounded-xl px-3 py-1 text-center align-middle transition-all duration-300"
            style={!locked
              ? { border: `2px dashed ${accent}70`, background: `${accent}12`, color: `${accent}90`, boxShadow: `0 0 18px ${accent}25` }
              : correct
                ? { border: "2px solid #22c55e", background: "rgba(34,197,94,0.2)", color: "#4ade80", boxShadow: "0 0 22px rgba(34,197,94,0.4)", animation: "sx-pop 0.4s both" }
                : { border: "2px solid #ef4444", background: "rgba(239,68,68,0.2)", color: "#f87171", animation: "sx-shake 0.4s" }}>
            {picked || "?"}
          </span>
          {after}
        </p>

        {/* word chips */}
        <div className="mt-8 flex flex-wrap gap-3">
          {it._opts.map((opt) => {
            const state = !locked ? "idle" : opt === it.answer ? "right" : opt === picked ? "wrong" : "dim";
            return (
              <button key={opt} onClick={() => choose(opt)} disabled={locked}
                className={[
                  "rounded-2xl border px-6 py-3.5 text-base font-black transition-all sm:text-lg",
                  state === "idle" && "bg-white/[0.05] text-white hover:-translate-y-1 active:scale-95",
                  state === "right" && "border-green bg-green/25 text-white",
                  state === "wrong" && "border-red bg-red/25 text-white",
                  state === "dim" && "border-white/8 bg-white/[0.02] text-white/35",
                ].filter(Boolean).join(" ")}
                style={state === "idle" ? { borderColor: `${accent}45`, boxShadow: `0 4px 14px rgba(0,0,0,0.3)` }
                  : state === "right" ? { boxShadow: "0 0 24px rgba(34,197,94,0.35)" } : undefined}>
                {opt}
              </button>
            );
          })}
        </div>

        {locked && !correct && (
          <p className="mt-5 rounded-2xl border border-cyan/40 bg-cyan/10 p-3.5 text-sm text-cyan" style={{ animation: "arena-in 0.3s ease both" }}>
            Richtig wäre: <b className="text-white">{it.answer}</b>
          </p>
        )}
      </div>

      {locked && (
        <button onClick={next}
          className="mt-5 w-full rounded-2xl px-6 py-4 text-base font-black text-white transition-transform hover:scale-[1.01] active:scale-[0.98]"
          style={{ background: `linear-gradient(135deg, ${accent}, #06b6d4)`, boxShadow: `0 0 30px ${accent}45, 0 12px 26px rgba(0,0,0,0.4)`, animation: "arena-in 0.3s ease both" }}>
          {idx + 1 < items.length ? "Weiter →" : "Abschließen ✦"}
        </button>
      )}
    </div>
  );
}
